import { ROUTES } from 'Constants';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { BookingDetailSection, SectionProps } from './BookingDetailSection';
import { upperCaseFirst } from 'Util/StringUtils';
import { stringNotEmpty } from 'Util/TypeGuards';
import { store } from 'Models/Store';
import { useIntl } from 'react-intl';
import { appConfigStore } from 'Models/AppConfigStore';
import GuestTag from 'Views/Components/_HumanWritten/Tag/GuestTag';

export function CustomerInfoSection({ booking, canEdit, onEdit, isInvoiced }: SectionProps) {
	const { userPhoneEnabled, displayCompanyNameInsteadOfName } = appConfigStore;
	const intl = useIntl();
	if (!booking.bookedSummary) {
		console.error('Booked summary missing, this should not happen');
		return <></>;
	}

	if (!(store.isStaff || store.isAdmin || store.isManager)) {
		return null;
	}

	const ownerFirst = upperCaseFirst(booking.user.firstName?.toLowerCase() ?? '');
	const ownerLast = upperCaseFirst(booking.user.lastName?.toLowerCase() ?? '');
	const ownerName = `${ownerFirst} ${ownerLast}`.trim();

	// Staff-UI display mode: when enabled and the account holder has a company name, show the company
	// name (verbatim) in place of their personal name on the clickable line. Otherwise the personal name.
	const ownerLinkText =
		displayCompanyNameInsteadOfName && stringNotEmpty(booking.user.companyName)
			? booking.user.companyName!.trim()
			: ownerName;
	const primaryFirst = upperCaseFirst(booking.bookedSummary.primaryFirstName?.toLowerCase() ?? '');
	const primaryLast = upperCaseFirst(booking.bookedSummary.primaryLastName?.toLowerCase() ?? '');

	// Show the primary booking contact ("name on booking") when it's actually
	// different from the account owner — staff usually want both visible when
	// staff or another customer booked on someone else's behalf.
	const hasDistinctPrimaryContact =
		stringNotEmpty(`${primaryFirst}${primaryLast}`) &&
		`${primaryFirst} ${primaryLast}`.trim().toLowerCase() !== `${ownerFirst} ${ownerLast}`.trim().toLowerCase();

	const sectionContent = (
		<div data-test-id="booking-customer-info">
			<p className="flex items-center gap-2">
				<Link to={ROUTES.USER_DETAIL.url(booking.userId, 'bookings')} className="customer-info hide-underline">
					{ownerLinkText}
				</Link>
				{booking.user.isGuest && <GuestTag />}
			</p>
			<p className="customer-info__contact">{booking.user.email?.toLowerCase() || '-'}</p>
			{userPhoneEnabled &&
				(stringNotEmpty(booking.user.phone) ? (
					<p className="customer-info__contact">{booking.user.phone}</p>
				) : (
					<p className="customer-info__contact customer-info__contact--empty">No phone</p>
				))}
			{hasDistinctPrimaryContact && (
				<p className="customer-info__contact" data-test-id="booking-primary-contact-info">
					<span className="text-gray-500">
						{intl.formatMessage({
							id: 'booking-details__customer-info__primary-contact__label',
							defaultMessage: 'Name on booking:',
						})}{' '}
					</span>
					{`${primaryFirst} ${primaryLast}`.trim()}
				</p>
			)}
		</div>
	);

	return (
		<BookingDetailSection
			sectionName={intl.formatMessage({
				id: 'booking-details__customer-info-section__label',
				defaultMessage: 'Account information',
			})}
			sectionContent={sectionContent}
			sectionClassName="customer-info"
			icon={{
				name: 'person',
				useMaterialIcons: true,
			}}
			modificationRules={{
				canEdit: canEdit,
				editType: 'other',
				onEdit: onEdit,
				isInvoiced: isInvoiced,
			}}
		/>
	);
}
